import streamApiClient from "./streamApiClient";

// Types matching backend entities
export type StreamStatus =
  | "pending"
  | "live"
  | "paused"
  | "completed"
  | "failed";
export type StreamerStatus = "idle" | "streaming";

export interface StreamPlatformConfig {
  youtube: boolean;
  twitch: boolean;
}

export interface CreateStreamDto {
  title: string;
  description: string;
  platforms: StreamPlatformConfig;
}

export interface StreamPlatformData {
  streamKey?: string;
  ingestUrl?: string;
  liveStreamId?: string;
  liveStreamURL?: string;
  streamUrl?: string;
  error?: string;
}

export interface Stream {
  id: string;
  userId: string;
  platforms: Record<string, StreamPlatformData>;
  metadata?: {
    title?: string;
    description?: string;
  };
  status: StreamStatus;
  createdAt: string;
  updatedAt: string;
}

export interface StreamerStatusResponse {
  status: StreamerStatus;
  activeStream?: Stream;
}

// Public stream listing (no auth required)
export interface PublicStreamData {
  id: string;
  userId: string;
  username: string;
  avatarUrl: string;
  title: string;
  status: "live" | "completed" | "pending";
  platform: "youtube" | "twitch";
  streamUrl: string;
  viewerCount: number;
  thumbnailUrl?: string;
  startedAt: string;
  endedAt?: string;
}

export const streamService = {
  /**
   * Create a stream on selected platforms
   * @param token Cognito Access Token
   * @param data Stream configuration data
   */
  createStream: async (
    token: string,
    data: CreateStreamDto
  ): Promise<Stream> => {
    return await streamApiClient("/stream/create", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
  },

  /**
   * Update the status of a stream
   * @param token Cognito Access Token
   * @param streamId Stream ID
   * @param status New Status (live, paused, completed, etc.)
   */
  updateStreamStatus: async (
    token: string,
    streamId: string,
    status: StreamStatus
  ): Promise<Stream> => {
    return await streamApiClient(`/stream/${streamId}/status`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Get Current User Streamer Status
   * @param token Cognito Access Token
   */
  getStreamerStatus: async (token: string): Promise<StreamerStatusResponse> => {
    return await streamApiClient("/stream/status", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  /**
   * Get all user's streams
   * @param token Cognito Access Token
   */
  getStreams: async (token: string): Promise<Stream[]> => {
    return await streamApiClient("/stream", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  // =============================================
  // PUBLIC ENDPOINTS (No Auth Required)
  // =============================================

  /**
   * Get all currently live streams (public)
   * Used by home page to display active streamers
   */
  getLiveStreams: async (): Promise<PublicStreamData[]> => {
    return await streamApiClient("/stream/live", {
      method: "GET",
    });
  },

  /**
   * Get past streams with pagination (public)
   * Used by home page to display completed streams
   */
  getPastStreams: async (
    limit = 20,
    offset = 0
  ): Promise<PublicStreamData[]> => {
    return await streamApiClient(
      `/stream/past?limit=${limit}&offset=${offset}`,
      {
        method: "GET",
      }
    );
  },

  /**
   * Get public status of a specific streamer
   * @param username The username to fetch status for
   */
  getStreamerPublicStatus: async (
    username: string
  ): Promise<PublicStreamData | null> => {
    try {
      return await streamApiClient(`/stream/${username}/status`, {
        method: "GET",
      });
    } catch (error) {
      console.error("Error fetching streamer status:", error);
      return null;
    }
  },
};
